import { Alert, Box, Button, Chip, Collapse, Divider, InputAdornment, LinearProgress, Link, MenuItem, TextField, Typography } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import { ItemList2 } from "./ItemList2";
import { MyListApi } from "../utils/restApi";
import { fetchTags, resetTags, selectVisibleTags } from "../slices/tagsSlice";
import { CategoryPicker } from "./ui/CategoryPicker";
import { formatLabel } from "../utils/itemUtil";
import {
    buildSearchBody,
    countActiveFilters,
    getFilterableProperties,
    parseFiltersParam,
    sanitizeFilters,
    serializeFiltersParam,
} from "../utils/browseSearch";

// Browse -> item -> dedicated Share Experience page.
// The applied search lives in the URL (category, q, f, run) so returning from
// an item restores it; the form holds a draft until Search is pressed.
export const Browse = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(state => state.login.user);
    const tags = useSelector(selectVisibleTags);
    const { status: tagsStatus, error: tagsError } = useSelector(state => state.tags);

    const [searchParams, setSearchParams] = useSearchParams();
    const category = searchParams.get('category') || '';
    const appliedName = searchParams.get('q') || '';
    const appliedFiltersParam = searchParams.get('f') || '';
    const hasRun = searchParams.get('run') === '1';

    const tag = useMemo(() => tags.find(t => t.name === category) || null, [tags, category]);
    const filterable = useMemo(() => getFilterableProperties(tag), [tag]);
    const appliedFilters = useMemo(() => sanitizeFilters(tag, parseFiltersParam(appliedFiltersParam)), [tag, appliedFiltersParam]);

    const [draftName, setDraftName] = useState(appliedName);
    const [draftFilters, setDraftFilters] = useState(appliedFilters);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [results, setResults] = useState({ status: 'idle', items: null, error: null });
    const [searchNonce, setSearchNonce] = useState(0);
    const requestRef = useRef(0);

    useEffect(() => {
        if (tagsStatus === 'idle') {
            dispatch(fetchTags());
        }
    }, [tagsStatus, dispatch]);

    // Keep the draft in step with the applied search (category switches,
    // back navigation, chip removal).
    useEffect(() => {
        setDraftName(appliedName);
    }, [appliedName, category]);

    useEffect(() => {
        setDraftFilters(appliedFilters);
    }, [appliedFilters]);

    // Run the applied search. Responses from superseded requests are ignored
    // so a slow earlier search can't replace current results.
    useEffect(() => {
        const requestId = ++requestRef.current;
        if (!hasRun || !tag) {
            setResults({ status: 'idle', items: null, error: null });
            return;
        }
        setResults({ status: 'loading', items: null, error: null });
        MyListApi.searchItems(buildSearchBody(tag.name, appliedName, appliedFilters))
            .then(data => {
                if (requestRef.current !== requestId) return;
                setResults({ status: 'succeeded', items: Array.isArray(data) ? data : (data?.items || []), error: null });
            })
            .catch(error => {
                if (requestRef.current !== requestId) return;
                setResults({ status: 'error', items: null, error: error?.message || 'Search failed.' });
            });
    }, [hasRun, tag, appliedName, appliedFilters, searchNonce]);

    const applySearch = (name, filters) => {
        const next = { category };
        const trimmed = (name || '').trim();
        if (trimmed) next.q = trimmed;
        const f = serializeFiltersParam(filters);
        if (f) next.f = f;
        next.run = '1';
        setSearchParams(next, { replace: true });
        setSearchNonce(n => n + 1);
    }

    const handleCategorySelect = selectedTag => {
        if (selectedTag.name === category) return;
        // Filters belong to a category's schema, so they reset on change.
        setDraftFilters({});
        setFiltersOpen(false);
        setSearchParams({ category: selectedTag.name }, { replace: true });
    }

    const handleSubmit = e => {
        e.preventDefault();
        if (!tag) return;
        applySearch(draftName, sanitizeFilters(tag, draftFilters));
    }

    const handleClear = () => {
        setDraftName('');
        setDraftFilters({});
        setSearchParams(category ? { category } : {}, { replace: true });
    }

    const handleDraftFilterChange = propertyName => e => {
        const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
        setDraftFilters(prev => ({ ...prev, [propertyName]: value }));
    }

    const handleRemoveApplied = (propertyName, value) => {
        const next = { ...appliedFilters, [propertyName]: (appliedFilters[propertyName] || []).filter(v => v !== value) };
        applySearch(appliedName, next);
    }

    const handleClearFilters = () => {
        setDraftFilters({});
        if (hasRun) applySearch(appliedName, {});
    }

    const handleRetryTags = () => {
        dispatch(resetTags());
    }

    const draftFilterCount = countActiveFilters(sanitizeFilters(tag, draftFilters));
    const appliedFilterCount = countActiveFilters(appliedFilters);
    const categoryLabel = tag ? formatLabel(tag.name) : '';
    const missingCategory = Boolean(category) && tagsStatus === 'succeeded' && !tag;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Box>
                <Typography component="h1" variant="h1">Find your next experience.</Typography>
                <Typography color="text.secondary" sx={{ mt: '8px' }}>
                    Pick a category, find the item, then open it to share your story.
                </Typography>
            </Box>

            <CategoryPicker
                tags={tags}
                status={tagsStatus}
                error={tagsError}
                selected={category}
                onSelect={handleCategorySelect}
                onRetry={handleRetryTags}
            />

            {missingCategory ? <Alert severity="warning">That category isn't available. Choose another one.</Alert> : null}

            <Box component="form" role="search" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', gap: '9px', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <TextField
                        id="browse-name"
                        label={tag ? `Search ${categoryLabel.toLowerCase()}` : 'Choose a category to search'}
                        placeholder="Search by name…"
                        value={draftName}
                        onChange={e => setDraftName(e.target.value)}
                        disabled={!tag}
                        fullWidth
                        inputProps={{ type: 'search' }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    />
                    <Button type="submit" variant="contained" disabled={!tag} sx={{ minHeight: 56, px: 3, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                        Search
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', mt: '10px' }}>
                    {filterable.length ?
                        <Button
                            variant="text"
                            startIcon={<TuneIcon />}
                            onClick={() => setFiltersOpen(!filtersOpen)}
                            aria-expanded={filtersOpen}
                            aria-controls="browse-filters"
                        >
                            {filtersOpen ? 'Hide filters' : 'More filters'}{draftFilterCount ? ` (${draftFilterCount})` : ''}
                        </Button>
                    : null}
                    {tag ? <Button variant="text" onClick={handleClear}>Clear</Button> : null}
                </Box>
                {filterable.length ?
                    <Collapse in={filtersOpen} unmountOnExit>
                        <Box
                            id="browse-filters"
                            sx={{ mt: '10px', p: '15px', bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: '12px' }}
                        >
                            {filterable.map(p => (
                                <TextField
                                    key={p.name}
                                    select
                                    size="small"
                                    label={formatLabel(p.name)}
                                    value={draftFilters[p.name] || []}
                                    onChange={handleDraftFilterChange(p.name)}
                                    SelectProps={{ multiple: true, renderValue: selected => selected.join(', ') }}
                                    helperText="Any of the selected values"
                                >
                                    {p.values.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </TextField>
                            ))}
                            <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                                <Button onClick={handleClearFilters} disabled={!draftFilterCount && !appliedFilterCount}>Clear filters</Button>
                                <Button type="submit" variant="outlined">Apply</Button>
                            </Box>
                        </Box>
                    </Collapse>
                : null}
            </Box>

            {hasRun && tag ?
                <Box component="section" aria-labelledby="browse-results-heading">
                    <Divider sx={{ mb: '20px' }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px 12px', mb: '12px' }}>
                        <Typography id="browse-results-heading" component="h2" variant="h2">{categoryLabel}</Typography>
                        <Typography variant="body2" color="text.secondary" aria-live="polite">
                            {results.status === 'succeeded' ? `${results.items.length} result${results.items.length === 1 ? '' : 's'}` : ''}
                        </Typography>
                    </Box>
                    {appliedName || appliedFilterCount ?
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', mb: '15px' }}>
                            {appliedName ? <Chip size="small" label={`Name: ${appliedName}`} onDelete={() => applySearch('', appliedFilters)} /> : null}
                            {Object.entries(appliedFilters).flatMap(([key, values]) => values.map(v => (
                                <Chip key={`${key}-${v}`} size="small" label={`${formatLabel(key)}: ${v}`} onDelete={() => handleRemoveApplied(key, v)} />
                            )))}
                            {appliedFilterCount ? <Button size="small" onClick={handleClearFilters}>Clear all filters</Button> : null}
                        </Box>
                    : null}
                    {results.status === 'loading' ? <LinearProgress aria-label="Searching" /> : null}
                    {results.status === 'error' ?
                        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setSearchNonce(n => n + 1)}>Retry</Button>}>
                            {results.error}
                        </Alert>
                    : null}
                    {results.status === 'succeeded' ?
                        <ItemList2 items={results.items} emptyText="No matches. Try a different name or clear your filters." />
                    : null}
                </Box>
            : null}

            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontWeight: 600 }}>Can't find it?</Typography>
                    {user ?
                        <Typography variant="body2" color="text.secondary">Add the missing item, then share your experience.</Typography>
                        : <Typography variant="body2" color="text.secondary">
                            <Link component={RouterLink} to="/login">Log in</Link> or <Link component={RouterLink} to="/sign-up">sign up</Link> to add missing items.
                        </Typography>
                    }
                </Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/create-item')} disabled={!user}>Create item</Button>
            </Box>
        </Box>
    )
}
